// Lieferregeln und Gutscheine – die reine Prüfung (docs/integration/bestell-app.md §19.4). Keine Datenbank, keine Uhr:
// Eingabe sind die Regeln eines Standorts, der Stand der Belegung und die Angaben der Bestellung; Ausgabe sind die
// Prüfhinweise und die von Merxo angewandten Beträge. Die Bestell-App rechnet bei gleichen Regeln genauso (shared/core/slots.ts,
// pricing.ts der App) – die Testfälle dort sind in checkout.test.ts sinngemäß übernommen.
//
//   * Merxo lehnt nie ab (C5): Jeder Verstoß ist ein Hinweis (`CheckoutIssue`), die Bestellung bleibt „Eingegangen“.
//   * Beträge (C6): Gebühr = min(App, Merxo), Rabatt = max(App, Merxo) – der Rabatt der App gilt höchstens bis zur Höhe, die der
//     Gutschein nach Merxo-Regeln ergäbe (ohne Mindestwert-Prüfung); ein unbekannter Code ergibt 0 € und einen Hinweis.
//   * Bestellschluss (C7) prüft gegen den Bestellzeitpunkt der App, wenn der glaubhaft ist (nicht in der Zukunft, höchstens 48 h
//     vor dem Eingang), sonst gegen den Eingang.
//   * Sind die Lieferregeln nicht eingerichtet (`configured: false`), prüft nichts etwas, und die Beträge der App gelten wie zuvor.
//     Hat nur der Standort keine eigenen Regeln (der Mandant schon), gelten dort nur Gutschein und Tragservice (mandantenweit, C2).
//   * Gutscheine gelten am Bestelltag (wie in der App), nicht am Liefertag.
import { formatDate, formatEuro, formatTime } from '@shared/format'
import { percentOf } from '@shared/money'
import { berlinTimeToDate, businessDate, weekdayOf, type IsoDate } from '@shared/time'
import { haversine, plzInfo, type GeoPoint } from './geo'
import { brand } from '@shared/brand'

export type SlotKind = 'delivery' | 'pickup'
export type CustomerKind = 'private' | 'business'

export const SLOT_KIND_LABELS: Record<SlotKind, string> = { delivery: 'Lieferung', pickup: 'Abholung' }
export const WEEKDAY_LABELS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'] as const
export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const

/** Vorgaben (C4) */
export const DEFAULT_DELIVERY_CUTOFF_MINUTES = 90
export const DEFAULT_PICKUP_CUTOFF_MINUTES = 30
export const DEFAULT_PICKUP_HOLD_HOURS = 48
/** so lange vor dem Eingang darf der Bestellzeitpunkt der App höchstens liegen (C7) */
export const PLACED_AT_MAX_AGE_MS = 48 * 3_600_000

export const ISSUE_CODES = [
  'slot_unknown',
  'slot_closed',
  'slot_cutoff',
  'slot_full',
  'zip_not_served',
  'min_order',
  'coupon_unknown',
  'coupon_inactive',
  'coupon_expired',
  'coupon_b2c_only',
  'coupon_min_order',
  'coupon_limit',
  'coupon_customer_limit',
  'fee_differs',
  'discount_differs',
] as const
export type IssueCode = (typeof ISSUE_CODES)[number]

export interface CheckoutIssue {
  code: IssueCode
  /** deutsch, für das Personal */
  message: string
  appCents?: number
  merxoCents?: number
}

export const ISSUE_LABELS: Record<IssueCode, string> = {
  slot_unknown: 'Fenster unbekannt',
  slot_closed: 'Fenster gesperrt',
  slot_cutoff: 'Bestellschluss überschritten',
  slot_full: 'Fenster voll',
  zip_not_served: 'Postleitzahl nicht im Liefergebiet',
  min_order: 'Mindestbestellwert nicht erreicht',
  coupon_unknown: 'Gutschein unbekannt',
  coupon_inactive: 'Gutschein nicht aktiv',
  coupon_expired: 'Gutschein abgelaufen',
  coupon_b2c_only: 'Gutschein nur für Privatkunden',
  coupon_min_order: 'Mindestwert des Gutscheins nicht erreicht',
  coupon_limit: 'Gutschein vergeben',
  coupon_customer_limit: 'Gutschein schon eingelöst',
  fee_differs: 'Gebühr weicht ab',
  discount_differs: 'Rabatt weicht ab',
}

// ---- Regeln ---------------------------------------------------------------------------------------------------------

export interface SlotRule {
  id: string
  /** 1 = Montag … 7 = Sonntag */
  weekday: number
  /** 'HH:MM' */
  start: string
  end: string
  capacity: number
}

export interface ExceptionRule {
  day: IsoDate
  kind: SlotKind | 'both'
  note: string | null
}

export interface ZoneRule {
  id: string
  name: string
  zips: readonly string[]
  feeCents: number
  minOrderCents: number
  /** null = nie frei */
  freeFromCents: number | null
}

export interface CouponRule {
  id: string
  code: string
  kind: 'percent' | 'fixed'
  value: number
  minOrderCents: number | null
  validFrom: IsoDate | null
  validUntil: IsoDate | null
  b2cOnly: boolean
  active: boolean
  maxRedemptions: number | null
  maxPerCustomer: number | null
  /** aktive Einlösungen insgesamt */
  redeemed: number
  /** davon durch den Kunden der Bestellung (0, wenn nicht bekannt) */
  redeemedByCustomer: number
}

/** Regeln eines Standorts. */
export interface LocationRules {
  /** false: Lieferregeln nicht eingerichtet – es wird nichts geprüft */
  configured: boolean
  deliverySlots: readonly SlotRule[]
  pickupSlots: readonly SlotRule[]
  exceptions: readonly ExceptionRule[]
  deliveryCutoffMinutes: number
  pickupCutoffMinutes: number
  pickupHoldHours: number
  zones: readonly ZoneRule[]
}

export const EMPTY_RULES: LocationRules = {
  configured: false,
  deliverySlots: [],
  pickupSlots: [],
  exceptions: [],
  deliveryCutoffMinutes: DEFAULT_DELIVERY_CUTOFF_MINUTES,
  pickupCutoffMinutes: DEFAULT_PICKUP_CUTOFF_MINUTES,
  pickupHoldHours: DEFAULT_PICKUP_HOLD_HOURS,
  zones: [],
}

// ---- Bausteine ------------------------------------------------------------------------------------------------------

export const normalizeCouponCode = (code: string | null | undefined): string => (code ?? '').trim().toUpperCase()
export const isCouponCode = (code: string): boolean => /^[A-Z0-9-]{3,24}$/.test(code)
/** 'HH:MM' aus 'HH:MM' oder 'HH:MM:SS' (time-Spalte) */
export const hm = (time: string): string => time.slice(0, 5)
export const windowText = (start: string, end: string): string => `${hm(start)}–${hm(end)}`
const dayText = (day: IsoDate, today: IsoDate): string => formatDate(day, { short: true, today })
const kindWord = (kind: SlotKind): string => (kind === 'pickup' ? 'Abholfenster' : 'Fenster')

export function cutoffMinutesOf(rules: LocationRules, kind: SlotKind): number {
  return kind === 'pickup' ? rules.pickupCutoffMinutes : rules.deliveryCutoffMinutes
}

export const slotsOf = (rules: LocationRules, kind: SlotKind): readonly SlotRule[] => (kind === 'pickup' ? rules.pickupSlots : rules.deliverySlots)

/** Ausnahmetag der Art an diesem Tag (an Ausnahmetagen gibt es keine Fenster). */
export function exceptionOn(rules: LocationRules, kind: SlotKind, day: IsoDate): ExceptionRule | null {
  return rules.exceptions.find((entry) => entry.day === day && (entry.kind === 'both' || entry.kind === kind)) ?? null
}

/** Vorlagen eines Tages nach Beginn – an einem Ausnahmetag keine. */
export function slotsOnDay(rules: LocationRules, kind: SlotKind, day: IsoDate): SlotRule[] {
  if (exceptionOn(rules, kind, day)) return []
  const weekday = weekdayOf(day)
  return slotsOf(rules, kind)
    .filter((slot) => slot.weekday === weekday)
    .sort((a, b) => a.start.localeCompare(b.start))
}

/** Bestellzeitpunkt der App, wenn glaubhaft (C7): nicht in der Zukunft, höchstens 48 h vor dem Eingang – sonst der Eingang. */
export function effectivePlacedAt(placedAt: Date | null | undefined, now: Date): Date {
  if (!placedAt || Number.isNaN(placedAt.getTime())) return now
  const age = now.getTime() - placedAt.getTime()
  return age < 0 || age > PLACED_AT_MAX_AGE_MS ? now : placedAt
}

export function zoneForZip(zones: readonly ZoneRule[], zip: string | null | undefined): ZoneRule | null {
  const wanted = (zip ?? '').trim()
  return wanted === '' ? null : (zones.find((zone) => zone.zips.includes(wanted)) ?? null)
}

/** Rabatt eines Gutscheins auf den Warenwert (ohne Pfand), gedeckelt auf den Warenwert (C8). */
export function couponDiscount(coupon: Pick<CouponRule, 'kind' | 'value'>, goodsGrossCents: number): number {
  const goods = Math.max(0, goodsGrossCents)
  return coupon.kind === 'percent' ? Math.min(goods, percentOf(goods, coupon.value * 100)) : Math.min(goods, Math.max(0, coupon.value))
}

/** Gebühr, die ein Gebiet für diesen Warenwert verlangt (Abholung: keine). */
export function zoneFee(zone: ZoneRule, goodsGrossCents: number, freeDelivery: boolean): number {
  if (freeDelivery) return 0
  return zone.freeFromCents !== null && goodsGrossCents >= zone.freeFromCents ? 0 : zone.feeCents
}

/** Farben der Liefergebiete – dieselbe Reihe wie in der Bestell-App (Einstellungen → Liefergebiete); das erste Gebiet ist blau. */
export const ZONE_COLORS = ['#1d58a0', '#f2a900', '#7c3aed', '#0d9488', '#db2777', '#ea580c', '#16a34a', '#0284c7'] as const

/** Farbe für ein neues Gebiet: die erste der Reihe, die noch kein Gebiet trägt (sonst wieder von vorn). */
export function nextZoneColor(used: readonly string[]): string {
  const taken = new Set(used.map((color) => color.toLowerCase()))
  return ZONE_COLORS.find((color) => !taken.has(color)) ?? ZONE_COLORS[used.length % ZONE_COLORS.length] ?? ZONE_COLORS[0]
}

/** Vorschau „Was zahlt die Kundschaft bei …?“ für ein Gebiet. */
export function zoneQuote(zone: ZoneRule, goodsGrossCents: number): { belowMinimum: boolean; missingCents: number; feeCents: number } {
  const missingCents = Math.max(0, zone.minOrderCents - goodsGrossCents)
  return { belowMinimum: missingCents > 0, missingCents, feeCents: zoneFee(zone, goodsGrossCents, false) }
}

/** Kartenmitte und Radius aus den bekannten Mittelpunkten der Postleitzahlen (C10); ohne bekannte Mittelpunkte beides null. */
export function zoneGeometry(zips: readonly string[]): { center: GeoPoint; radiusM: number } | null {
  const points = zips.flatMap((zip) => {
    const info = plzInfo(zip)
    return info ? [info.center] : []
  })
  if (points.length === 0) return null
  const center = { lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length, lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length }
  const reach = Math.max(...points.map((point) => haversine(center, point)))
  // ein Mittelpunkt steht für ein ganzes Postleitzahlgebiet: mindestens 1,5 km, sonst 1 km Rand
  const radiusM = Math.round(Math.max(1500, reach + 1000) / 100) * 100
  return { center: { lat: Math.round(center.lat * 1e6) / 1e6, lng: Math.round(center.lng * 1e6) / 1e6 }, radiusM }
}

// ---- Gutschein prüfen (live, C8) --------------------------------------------------------------------------------------

export type CouponReason = 'unknown' | 'inactive' | 'not_yet_valid' | 'expired' | 'b2c_only' | 'limit' | 'customer_limit' | 'min_order'

export interface CouponCheck {
  valid: boolean
  reason: CouponReason | null
  /** deutsch, für die Kundschaft */
  message: string | null
}

export interface CouponCheckInput {
  customerType: CustomerKind
  goodsGrossCents: number
  day: IsoDate
}

/** Alle Gründe, aus denen der Gutschein nicht (voll) gilt – in der Reihenfolge ihrer Schwere. */
export function couponReasons(coupon: CouponRule | null, input: CouponCheckInput): CouponReason[] {
  if (!coupon) return ['unknown']
  const reasons: CouponReason[] = []
  if (!coupon.active) reasons.push('inactive')
  if (coupon.validFrom && coupon.validFrom > input.day) reasons.push('not_yet_valid')
  if (coupon.validUntil && coupon.validUntil < input.day) reasons.push('expired')
  if (coupon.b2cOnly && input.customerType === 'business') reasons.push('b2c_only')
  if (coupon.maxRedemptions !== null && coupon.redeemed >= coupon.maxRedemptions) reasons.push('limit')
  if (coupon.maxPerCustomer !== null && coupon.redeemedByCustomer >= coupon.maxPerCustomer) reasons.push('customer_limit')
  if (coupon.minOrderCents && input.goodsGrossCents < coupon.minOrderCents) reasons.push('min_order')
  return reasons
}

/** Text für die Kundschaft (wie in der App; neu: „vergeben“ und „schon eingelöst“). */
export function couponMessage(reason: CouponReason, coupon: CouponRule | null, code: string, input: CouponCheckInput): string {
  const shown = coupon?.code ?? (normalizeCouponCode(code) || code)
  switch (reason) {
    case 'unknown':
    case 'inactive':
      return `Der Gutscheincode „${shown}“ ist ungültig.`
    case 'not_yet_valid':
      return `Der Gutschein „${shown}“ gilt erst ab ${formatDate(coupon?.validFrom ?? input.day)}.`
    case 'expired':
      return `Der Gutschein „${shown}“ ist abgelaufen.`
    case 'b2c_only':
      return `Der Gutschein „${shown}“ gilt nur für Privatkunden.`
    case 'limit':
      return 'Dieser Gutschein ist bereits vergeben.'
    case 'customer_limit':
      return 'Sie haben diesen Gutschein bereits eingelöst.'
    case 'min_order': {
      const min = coupon?.minOrderCents ?? 0
      return `Der Gutschein „${shown}“ gilt ab ${formatEuro(min)} Warenwert. Es fehlen noch ${formatEuro(Math.max(0, min - input.goodsGrossCents))}.`
    }
  }
}

/** Prüfergebnis für `sales.channel.checkCoupon`: der erste blockierende Grund; fehlender Mindestwert ist nur eine Warnung (valid, Rabatt 0). */
export function checkCoupon(coupon: CouponRule | null, code: string, input: CouponCheckInput): CouponCheck & { discountCents: number } {
  const reasons = couponReasons(coupon, input)
  const blocking = reasons.find((reason) => reason !== 'min_order')
  if (blocking) return { valid: false, reason: blocking, message: couponMessage(blocking, coupon, code, input), discountCents: 0 }
  if (reasons.includes('min_order')) return { valid: true, reason: 'min_order', message: couponMessage('min_order', coupon, code, input), discountCents: 0 }
  return { valid: true, reason: null, message: null, discountCents: coupon ? couponDiscount(coupon, input.goodsGrossCents) : 0 }
}

// ---- Fenster prüfen ---------------------------------------------------------------------------------------------------

export interface SlotCheckInput {
  rules: LocationRules
  kind: SlotKind
  day: IsoDate | null
  timeFrom: string | null
  timeTo: string | null
  now: Date
  placedAt?: Date | null
  /**
   * true: `placedAt` ist schon der geprüfte Bestellzeitpunkt (Neubewertung nach einer Terminänderung, gespeichert bei der
   * Bestellung) – nicht noch einmal gegen „jetzt“ begrenzen, sonst gälte zwei Tage nach der Bestellung die Uhrzeit der Änderung.
   */
  placedAtFixed?: boolean
  /** Aufträge im Fenster einschließlich dieses */
  booked: number
}

/** Hinweise zum Fenster einer Bestellung (C3, C4, C7). */
export function slotIssues(input: SlotCheckInput): CheckoutIssue[] {
  const { rules, kind, day, now } = input
  const word = kindWord(kind)
  const today = businessDate(now)
  if (!day || !input.timeFrom || !input.timeTo) {
    return [{ code: 'slot_unknown', message: `Die Bestellung nennt ${day ? `kein ${word}` : 'keinen Termin'}.` }]
  }
  const start = hm(input.timeFrom)
  const end = hm(input.timeTo)
  const label = `${word} ${windowText(start, end)} am ${dayText(day, today)}`
  const closedDay = exceptionOn(rules, kind, day)
  if (closedDay) {
    return [{ code: 'slot_closed', message: `Am ${dayText(day, today)} gibt es keine ${kind === 'pickup' ? 'Abholung' : 'Lieferung'}${closedDay.note ? ` (${closedDay.note})` : ''}.` }]
  }
  const template = slotsOnDay(rules, kind, day).find((slot) => slot.start === start && slot.end === end)
  if (!template) return [{ code: 'slot_unknown', message: `${label} gibt es nicht (keine Vorlage für ${kind === 'pickup' ? 'diesen Abholzeitraum' : 'dieses Fenster'}).` }]
  if (template.capacity === 0) return [{ code: 'slot_closed', message: `${label} ist gesperrt.` }]
  const issues: CheckoutIssue[] = []
  const placed = input.placedAtFixed && input.placedAt && !Number.isNaN(input.placedAt.getTime()) ? input.placedAt : effectivePlacedAt(input.placedAt, now)
  const cutoffAt = berlinTimeToDate(day, start).getTime() - cutoffMinutesOf(rules, kind) * 60_000
  if (placed.getTime() > cutoffAt) {
    issues.push({ code: 'slot_cutoff', message: `Bestellschluss für ${label} war ${formatTime(new Date(cutoffAt))} Uhr, bestellt wurde um ${formatTime(placed)} Uhr (${formatDate(placed, { short: true, today })}).` })
  }
  if (input.booked > template.capacity) issues.push({ code: 'slot_full', message: `${label} ist voll (${input.booked} von ${template.capacity}).` })
  return issues
}

// ---- Bestellung prüfen ------------------------------------------------------------------------------------------------

export interface CheckoutInput {
  /** Regeln des Ausgangsstandorts; `configured: false` = der Standort hat keine eigenen Lieferregeln (Fenster, Gebiete) */
  rules: LocationRules
  /**
   * Lieferregeln des Mandanten eingerichtet (Gutscheine, Tragservice gelten mandantenweit, C2). Ohne Angabe wie `rules.configured`.
   * Ist nur der Mandant eingerichtet, nicht aber der Standort, prüft Merxo dort nur Gutschein und Tragservice.
   */
  tenantConfigured?: boolean
  coupons: readonly CouponRule[]
  /** Pauschale des Tragservice, brutto */
  carryFeeCents: number
  fulfillment: SlotKind
  customerType: CustomerKind
  /** „Lieferung frei“ am Kunden */
  freeDelivery: boolean
  now: Date
  placedAt?: Date | null
  day: IsoDate | null
  timeFrom: string | null
  timeTo: string | null
  /** Aufträge im Fenster einschließlich dieses */
  booked: number
  zip: string | null
  /** Warenwert brutto ohne Pfand */
  goodsGrossCents: number
  app: {
    deliveryFeeCents: number
    carryCents: number
    carryService: boolean
    couponCode: string | null
    couponDiscountCents: number
  }
}

export interface CheckoutEvaluation {
  issues: CheckoutIssue[]
  zoneId: string | null
  /** von Merxo angewandt (C6) */
  deliveryFeeCents: number
  carryCents: number
  discountCents: number
  couponId: string | null
  /** Einlösung zu buchen (C9): Gutschein bekannt und Rabatt angewandt */
  redemption: { couponId: string; flags: ('over_limit' | 'over_customer_limit')[] } | null
}

export function evaluateCheckout(input: CheckoutInput): CheckoutEvaluation {
  const { app } = input
  const base: CheckoutEvaluation = {
    issues: [],
    zoneId: null,
    deliveryFeeCents: app.deliveryFeeCents,
    carryCents: app.carryCents,
    discountCents: app.couponDiscountCents,
    couponId: null,
    redemption: null,
  }
  if (!(input.tenantConfigured ?? input.rules.configured)) return base
  const issues: CheckoutIssue[] = []
  const delivery = input.fulfillment === 'delivery'
  // Fenster und Gebiete hängen am Standort (C2): ohne eigene Regeln des Standorts keine Hinweise dazu, die Gebühr der App bleibt
  const local = input.rules.configured

  if (local) issues.push(...slotIssues({ rules: input.rules, kind: input.fulfillment, day: input.day, timeFrom: input.timeFrom, timeTo: input.timeTo, now: input.now, placedAt: input.placedAt ?? null, booked: input.booked }))

  // ---- Liefergebühr, Gebiet, Mindestwert ----
  let deliveryFeeCents = app.deliveryFeeCents
  let zoneId: string | null = null
  if (delivery && local) {
    const zone = zoneForZip(input.rules.zones, input.zip)
    if (!zone) {
      issues.push({ code: 'zip_not_served', message: input.zip ? `Die Postleitzahl ${input.zip} liegt in keinem Liefergebiet.` : 'Die Lieferanschrift nennt keine Postleitzahl.' })
    } else {
      zoneId = zone.id
      if (input.goodsGrossCents < zone.minOrderCents) {
        issues.push({ code: 'min_order', message: `Mindestbestellwert für ${zone.name}: ${formatEuro(zone.minOrderCents)}, Warenwert ${formatEuro(input.goodsGrossCents)}.` })
      }
      const merxo = zoneFee(zone, input.goodsGrossCents, input.freeDelivery)
      deliveryFeeCents = Math.min(app.deliveryFeeCents, merxo)
      if (app.deliveryFeeCents !== merxo) {
        issues.push({ code: 'fee_differs', message: `Liefergebühr: App ${formatEuro(app.deliveryFeeCents)}, ${brand.name} ${formatEuro(merxo)} – es gilt ${formatEuro(deliveryFeeCents)}.`, appCents: app.deliveryFeeCents, merxoCents: merxo })
      }
    }
  } else if (!delivery && app.deliveryFeeCents > 0) {
    // Abholung: keine Gebühr
    deliveryFeeCents = 0
    issues.push({ code: 'fee_differs', message: `Liefergebühr: App ${formatEuro(app.deliveryFeeCents)}, bei Abholung fällt keine an – es gilt ${formatEuro(0)}.`, appCents: app.deliveryFeeCents, merxoCents: 0 })
  }

  // ---- Tragservice ----
  let carryCents = app.carryCents
  if (app.carryService || app.carryCents > 0) {
    const merxo = delivery ? input.carryFeeCents : 0
    carryCents = Math.min(app.carryCents, merxo)
    if (app.carryCents !== merxo) {
      issues.push({ code: 'fee_differs', message: `Tragservice: App ${formatEuro(app.carryCents)}, ${brand.name} ${formatEuro(merxo)} – es gilt ${formatEuro(carryCents)}.`, appCents: app.carryCents, merxoCents: merxo })
    }
  }

  // ---- Gutschein ----
  let discountCents = 0
  let couponId: string | null = null
  let redemption: CheckoutEvaluation['redemption'] = null
  const code = normalizeCouponCode(app.couponCode)
  if (code !== '' || app.couponDiscountCents > 0) {
    // gültig am Bestelltag wie in der App (die prüft „heute“ beim Bestellen, checkCoupon mit day = heute) – nicht am Liefertag
    const day = businessDate(effectivePlacedAt(input.placedAt ?? null, input.now))
    const coupon = code === '' ? null : (input.coupons.find((entry) => normalizeCouponCode(entry.code) === code) ?? null)
    const check: CouponCheckInput = { customerType: input.customerType, goodsGrossCents: input.goodsGrossCents, day }
    if (!coupon) {
      issues.push({
        code: 'coupon_unknown',
        message: code === '' ? 'Die Bestellung nennt einen Rabatt, aber keinen Gutscheincode – es wird kein Rabatt angewandt.' : `Den Gutscheincode „${code}“ gibt es nicht – es wird kein Rabatt angewandt.`,
      })
      discountCents = 0
      if (app.couponDiscountCents > 0) {
        issues.push({ code: 'discount_differs', message: `Rabatt: App ${formatEuro(app.couponDiscountCents)}, ${brand.name} ${formatEuro(0)} – es gilt ${formatEuro(0)}.`, appCents: app.couponDiscountCents, merxoCents: 0 })
      }
    } else {
      couponId = coupon.id
      const reasons = couponReasons(coupon, check)
      // Merxo selbst gewährt nur, wenn nichts dagegen spricht; die Grenzen zählen nicht dagegen (C9: trotzdem buchen und markieren)
      const blocked = reasons.some((reason) => reason === 'inactive' || reason === 'not_yet_valid' || reason === 'expired' || reason === 'b2c_only' || reason === 'min_order')
      const formula = couponDiscount(coupon, input.goodsGrossCents)
      const merxo = blocked ? 0 : formula
      // der Rabatt der App gilt bis zur Höhe, die der Gutschein höchstens ergäbe (ohne Mindestwert-Prüfung)
      const accepted = Math.min(app.couponDiscountCents, formula)
      discountCents = Math.max(accepted, merxo)
      const shown = coupon.code
      for (const reason of reasons) {
        if (reason === 'inactive') issues.push({ code: 'coupon_inactive', message: `Der Gutschein „${shown}“ ist nicht aktiv.` })
        else if (reason === 'not_yet_valid') issues.push({ code: 'coupon_inactive', message: `Der Gutschein „${shown}“ gilt erst ab ${formatDate(coupon.validFrom as string)}.` })
        else if (reason === 'expired') issues.push({ code: 'coupon_expired', message: `Der Gutschein „${shown}“ ist seit dem ${formatDate(coupon.validUntil as string)} abgelaufen.` })
        else if (reason === 'b2c_only') issues.push({ code: 'coupon_b2c_only', message: `Der Gutschein „${shown}“ gilt nur für Privatkunden.` })
        else if (reason === 'min_order') issues.push({ code: 'coupon_min_order', message: `Der Gutschein „${shown}“ gilt ab ${formatEuro(coupon.minOrderCents ?? 0)} Warenwert, der Warenwert beträgt ${formatEuro(input.goodsGrossCents)}.` })
      }
      if (app.couponDiscountCents !== merxo) {
        issues.push({ code: 'discount_differs', message: `Rabatt: App ${formatEuro(app.couponDiscountCents)}, ${brand.name} ${formatEuro(merxo)} – es gilt ${formatEuro(discountCents)}.`, appCents: app.couponDiscountCents, merxoCents: merxo })
      }
      if (discountCents > 0) {
        const flags: ('over_limit' | 'over_customer_limit')[] = []
        if (reasons.includes('limit')) {
          flags.push('over_limit')
          issues.push({ code: 'coupon_limit', message: `Der Gutschein „${shown}“ ist schon ${coupon.redeemed}-mal eingelöst (Grenze ${coupon.maxRedemptions}) – diese Einlösung ist eine zu viel.` })
        }
        if (reasons.includes('customer_limit')) {
          flags.push('over_customer_limit')
          issues.push({ code: 'coupon_customer_limit', message: `Der Gutschein „${shown}“ wurde von diesem Kunden schon ${coupon.redeemedByCustomer}-mal eingelöst (Grenze ${coupon.maxPerCustomer} je Kunde).` })
        }
        redemption = { couponId: coupon.id, flags }
      }
    }
  }

  return { issues, zoneId, deliveryFeeCents, carryCents, discountCents, couponId, redemption }
}

/** Hinweise einer Neubewertung nach einer Terminänderung: die alten Hinweise zum Fenster fallen weg, die neuen kommen hinzu. */
export function replaceSlotIssues(old: readonly CheckoutIssue[], fresh: readonly CheckoutIssue[]): CheckoutIssue[] {
  return [...old.filter((issue) => !issue.code.startsWith('slot_')), ...fresh]
}

// ---- Telefonbestellung in Merxo (S1, docs/integration/bestell-app.md §20) -----------------------------------------------------

export type SlotState = 'free' | 'almost' | 'full' | 'over' | 'blocked' | 'adhoc'

/** Zustand eines Fensters: wie die Ampel der Belegung (Einstellungen → Lieferregeln). */
export function slotState(capacity: number, booked: number): SlotState {
  if (capacity === 0) return 'blocked'
  if (booked > capacity) return 'over'
  if (booked === capacity) return 'full'
  return capacity - booked <= 1 || booked / capacity >= 0.8 ? 'almost' : 'free'
}

export interface ProposalInput extends Omit<CheckoutInput, 'app'> {
  /** Tragservice gewünscht */
  carryService: boolean
  /** Gutscheincode (frei getippt) oder null */
  couponCode: string | null
}

export interface CheckoutProposal {
  /** Hinweise der Prüfung (Fenster, Gebiet, Mindestwert, Gutschein) – ohne Abweichungen der Beträge: es gibt keine App-Beträge */
  issues: CheckoutIssue[]
  zone: ZoneRule | null
  /** was die Regeln verlangen: Liefergebühr, Tragservice und der Rabatt des Gutscheins (alles brutto, positiv) */
  deliveryFeeCents: number
  carryCents: number
  discountCents: number
  couponId: string | null
  redemption: CheckoutEvaluation['redemption']
}

/**
 * Was die Regeln für eine Bestellung am Telefon vorschlagen. Es gibt keine Beträge einer App, mit denen verglichen würde: Die
 * Gebühr ergibt sich aus dem Gebiet der Postleitzahl, dem Warenwert und „Lieferung frei“, der Tragservice aus der Pauschale, der
 * Rabatt aus dem Gutschein. Die Prüfhinweise sind dieselben wie bei der Bestell-App (`evaluateCheckout`) – das Personal darf
 * trotzdem alles übersteuern (S1: nichts davon ist Pflicht).
 */
export function proposeCheckout(input: ProposalInput): CheckoutProposal {
  const delivery = input.fulfillment === 'delivery'
  const local = input.rules.configured
  const zone = delivery && local ? zoneForZip(input.rules.zones, input.zip) : null
  const deliveryFee = zone ? zoneFee(zone, input.goodsGrossCents, input.freeDelivery) : 0
  const carry = delivery && input.carryService ? input.carryFeeCents : 0
  const code = normalizeCouponCode(input.couponCode)
  let discount = 0
  if (code !== '') {
    const coupon = input.coupons.find((entry) => normalizeCouponCode(entry.code) === code) ?? null
    if (coupon) {
      const day = businessDate(effectivePlacedAt(input.placedAt ?? null, input.now))
      const reasons = couponReasons(coupon, { customerType: input.customerType, goodsGrossCents: input.goodsGrossCents, day })
      const blocked = reasons.some((reason) => reason === 'inactive' || reason === 'not_yet_valid' || reason === 'expired' || reason === 'b2c_only' || reason === 'min_order')
      discount = blocked ? 0 : couponDiscount(coupon, input.goodsGrossCents)
    }
  }
  const { carryService, couponCode: _couponCode, ...rest } = input
  const evaluation = evaluateCheckout({ ...rest, app: { deliveryFeeCents: deliveryFee, carryCents: carry, carryService, couponCode: code === '' ? null : code, couponDiscountCents: discount } })
  return { issues: evaluation.issues, zone, deliveryFeeCents: evaluation.deliveryFeeCents, carryCents: evaluation.carryCents, discountCents: evaluation.discountCents, couponId: evaluation.couponId, redemption: evaluation.redemption }
}
